import React, { useRef, useState } from "react";
import { Loader2, Upload, FileText, Trash2, CheckCircle2, AlertTriangle, Calendar } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { fileToCompressedDataUri } from "@/lib/imageUpload";
import { useI18n } from "@/contexts/I18nContext";
import { formatApiErrorDetail } from "@/lib/api";

const REQUIRED = ["TRADE_LICENSE", "TAX_CARD", "MEDICAL_PERMIT"];

/**
 * Shared KYC upload UI used by both the Provider self-service page
 * and the admin Provider Detail Sheet.
 *
 * Props:
 *   summary: KYC summary object from backend
 *   readOnly: bool — disables uploads (e.g. PENDING)
 *   uploadFn: ({doc_type, file_url, expiry_date?, notes?}) => Promise<doc>
 *   removeFn: (id) => Promise<void>
 *   onChange: () => void (called after successful upload/delete)
 *   testIdPrefix: e.g. "kyc" or "admin-kyc"
 */
export default function KycDocumentsPanel({ summary, readOnly, uploadFn, removeFn, onChange, testIdPrefix = "kyc" }) {
    const { t } = useI18n();
    const [busyType, setBusyType] = useState(null);
    const inputRefs = useRef({});

    const docsByType = {};
    (summary.documents || []).forEach((d) => {
        docsByType[d.doc_type] = docsByType[d.doc_type] || [];
        docsByType[d.doc_type].push(d);
    });

    const trigger = (docType) => {
        if (readOnly) return;
        inputRefs.current[docType]?.click();
    };

    const onPick = async (docType, file) => {
        if (!file) return;
        setBusyType(docType);
        try {
            let dataUri;
            if (file.type === "application/pdf") {
                dataUri = await new Promise((resolve, reject) => {
                    const reader = new FileReader();
                    reader.onload = () => resolve(reader.result);
                    reader.onerror = () => reject(new Error("Could not read this file."));
                    reader.readAsDataURL(file);
                });
                if (dataUri.length * 0.75 > 2_500_000) throw new Error(t("kyc.errors.fileTooLarge"));
            } else if (file.type.startsWith("image/")) {
                dataUri = await fileToCompressedDataUri(file, { maxDim: 1600, quality: 0.85 });
            } else {
                throw new Error(t("kyc.errors.unsupportedType"));
            }
            await uploadFn({ doc_type: docType, file_url: dataUri });
            toast.success(t("kyc.toasts.uploaded"));
            onChange?.();
        } catch (err) {
            toast.error(formatApiErrorDetail(err?.response?.data?.detail) || err.message);
        } finally {
            setBusyType(null);
        }
    };

    const onRemove = async (doc) => {
        try {
            await removeFn(doc.id);
            toast.success(t("kyc.toasts.removed"));
            onChange?.();
        } catch (err) {
            toast.error(formatApiErrorDetail(err?.response?.data?.detail) || err.message);
        }
    };

    return (
        <div className="space-y-3" data-testid={`${testIdPrefix}-panel`}>
            {REQUIRED.map((docType) => {
                const items = docsByType[docType] || [];
                const isMissing = items.length === 0;
                return (
                    <div
                        key={docType}
                        className={`rounded-2xl border ${isMissing ? "border-warning/40 bg-warning/5" : "border-border bg-card"} p-4`}
                        data-testid={`${testIdPrefix}-section-${docType}`}
                    >
                        <input
                            ref={(el) => { inputRefs.current[docType] = el; }}
                            type="file"
                            accept="image/*,application/pdf"
                            className="hidden"
                            onChange={(e) => {
                                const file = e.target.files?.[0];
                                e.target.value = "";
                                onPick(docType, file);
                            }}
                            data-testid={`${testIdPrefix}-input-${docType}`}
                        />
                        <div className="flex items-start gap-3">
                            <div className="icon-tile h-10 w-10 shrink-0">
                                <FileText className="h-5 w-5" />
                            </div>
                            <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 flex-wrap">
                                    <h3 className="text-sm font-semibold">{t(`kyc.docTypes.${docType}`)}</h3>
                                    {isMissing
                                        ? <Badge variant="outline" className="border-warning/40 text-warning">{t("kyc.required")}</Badge>
                                        : <Badge variant="outline" className="border-success/40 text-success">{t("kyc.uploaded", { n: items.length })}</Badge>}
                                </div>
                                <p className="text-xs text-muted-foreground mt-0.5">{t(`kyc.docHints.${docType}`)}</p>
                                {items.length > 0 && (
                                    <div className="mt-3 grid sm:grid-cols-2 gap-2">
                                        {items.map((d) => (
                                            <DocPreview key={d.id} doc={d} readOnly={readOnly} onRemove={onRemove} t={t} testIdPrefix={testIdPrefix} />
                                        ))}
                                    </div>
                                )}
                            </div>
                            {!readOnly && (
                                <Button
                                    size="sm"
                                    variant={isMissing ? "default" : "outline"}
                                    onClick={() => trigger(docType)}
                                    disabled={busyType === docType}
                                    data-testid={`${testIdPrefix}-upload-${docType}`}
                                >
                                    {busyType === docType
                                        ? <Loader2 className="h-4 w-4 me-1 animate-spin" />
                                        : <Upload className="h-4 w-4 me-1" />}
                                    {t(items.length > 0 ? "kyc.uploadAnother" : "kyc.upload")}
                                </Button>
                            )}
                        </div>
                    </div>
                );
            })}
        </div>
    );
}

function DocPreview({ doc, readOnly, onRemove, t, testIdPrefix }) {
    const isPdf = (doc.file_url || "").startsWith("data:application/pdf") || /\.pdf(\?|$)/i.test(doc.file_url || "");
    return (
        <div className="rounded-xl border border-border overflow-hidden bg-muted/40 group" data-testid={`${testIdPrefix}-doc-${doc.id}`}>
            <div className="aspect-[4/3] bg-card flex items-center justify-center overflow-hidden">
                {isPdf ? (
                    <a
                        href={doc.file_url}
                        target="_blank"
                        rel="noreferrer"
                        className="flex flex-col items-center gap-1 text-muted-foreground hover:text-foreground"
                    >
                        <FileText className="h-8 w-8" />
                        <span className="text-[11px]">{t("kyc.openPdf")}</span>
                    </a>
                ) : (
                    <img src={doc.file_url} alt="" className="h-full w-full object-cover" />
                )}
            </div>
            <div className="px-2.5 py-1.5 flex items-center gap-2">
                {doc.expiry_date && (
                    <span className="text-[10px] text-muted-foreground inline-flex items-center gap-1">
                        <Calendar className="h-3 w-3" />
                        {doc.expiry_date}
                    </span>
                )}
                {!readOnly && (
                    <Button
                        size="icon"
                        variant="ghost"
                        className="ms-auto h-7 w-7 opacity-0 group-hover:opacity-100 transition-opacity"
                        onClick={() => onRemove(doc)}
                        data-testid={`${testIdPrefix}-doc-remove-${doc.id}`}
                        aria-label="Remove"
                    >
                        <Trash2 className="h-3.5 w-3.5 text-destructive" />
                    </Button>
                )}
            </div>
        </div>
    );
}
